import { useState } from 'react';
import { Upload, UploadProps, Button, message } from 'antd';
import { CameraIcon, PlusIcon, TrayArrowUpIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import {
  AssetUploaderClient,
  normalizeAssetUploadError,
  prepareAssetUploadFile,
} from '@admin-panel/providers';
import { AssetUsage } from '@admin-panel/types';

type UploadRequestOption = Parameters<NonNullable<UploadProps['customRequest']>>[0];
type BeforeUploadFile = Parameters<NonNullable<UploadProps['beforeUpload']>>[0];
type UploadRequestFile = UploadRequestOption['file'];

interface IAssetUploadProps extends UploadProps {
  usage: AssetUsage;
  enabledDragDrop?: boolean;
  label?: string;
  disabled?: boolean;
  isLoading?: boolean;
  camera?: boolean;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
}

const AssetUploader = (props: IAssetUploadProps) => {
  const client = new AssetUploaderClient();

  const [loading, setLoading] = useState(false);

  const getBrowserFile = (file: UploadRequestFile | File) => {
    if (file instanceof File) {
      return file;
    }

    throw new Error('Unsupported upload file type.');
  };

  const uploadBrowserFile = async (
    file: File,
    callbacks?: Pick<UploadRequestOption, 'onError' | 'onSuccess'>,
  ) => {
    const { onError, onSuccess } = callbacks ?? {};

    const handleUploadError = (error: unknown) => {
      const uploadError = normalizeAssetUploadError(error);
      onError?.(uploadError);
      message.error(uploadError.message);
      props.onUploadEnd?.();
      setLoading(false);
    };

    try {
      setLoading(true);
      props.onUploadStart?.();
      const preparedFile = await prepareAssetUploadFile(file);
      await client.uploadFile({
        file: preparedFile,
        usage: props.usage,
        onSuccess: uploadedFile => {
          onSuccess?.(uploadedFile);
          props.onUploadEnd?.();
          setLoading(false);
        },
        onError: handleUploadError,
        source: 'ASSET',
      });
    } catch (error: unknown) {
      handleUploadError(error);
    }
  };

  const handleCameraClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        uploadBrowserFile(file);
      }
    };
    input.click();
  };

  const uploadFileToCDN = async ({ file, onError, onSuccess }: UploadRequestOption) => {
    try {
      await uploadBrowserFile(getBrowserFile(file), { onError, onSuccess });
    } catch (error: unknown) {
      const uploadError = normalizeAssetUploadError(error);
      onError?.(uploadError);
      message.error(uploadError.message);
      props.onUploadEnd?.();
      setLoading(false);
    }
  };

  const beforeUpload: UploadProps['beforeUpload'] = async (file, fileList) => {
    try {
      const preparedFile = await prepareAssetUploadFile(file as File);

      if (!props.beforeUpload) {
        return preparedFile;
      }

      const nextResult = await props.beforeUpload(preparedFile as BeforeUploadFile, fileList);

      if (nextResult === false || nextResult === Upload.LIST_IGNORE) {
        return nextResult;
      }

      return nextResult ?? preparedFile;
    } catch (error: unknown) {
      message.error(normalizeAssetUploadError(error).message);
      return Upload.LIST_IGNORE;
    }
  };

  const getUploadButton = () => {
    if (props.disabled) return <Button disabled />;

    if (props.enabledDragDrop) {
      return (
        <p className="ant-upload-drag-icon">
          <TrayArrowUpIcon />
          <div style={{ fontSize: 10 }}>Drag and Drop file here</div>
        </p>
      );
    } else if (props.camera) {
      return <CameraIcon onClick={handleCameraClick} />;
    } else {
      if (props.listType === 'text') {
        return (
          <Button icon={<UploadSimpleIcon />} disabled={props.disabled}>
            {props.label ? props.label : 'Upload'}
          </Button>
        );
      } else {
        return (
          <button style={{ padding: 24, border: 'none' }} type="button" disabled={props.disabled}>
            <PlusIcon />
            <div style={{ marginTop: 8 }}>Upload</div>
          </button>
        );
      }
    }
  };

  const Uploader = props.enabledDragDrop ? Upload.Dragger : Upload;

  return (
    <Uploader
      {...props}
      disabled={loading || props.disabled || props.isLoading}
      name="uploadList"
      listType={props.listType}
      customRequest={uploadFileToCDN}
      beforeUpload={beforeUpload}
      itemRender={props.itemRender}
      multiple={props.multiple}
    >
      {getUploadButton()}
    </Uploader>
  );
};

export default AssetUploader;
